import React, { useRef } from "react";
import { Instagram, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Customer Reels — "Müşteri Memnuniyetleri"
 * To update content, just edit the REELS array below.
 *
 * Each item shape:
 *   { title: string, image: string, instagramUrl: string }
 *
 * Use "#" for instagramUrl if no real Reels link is available yet.
 */
const REELS = [
  {
    title: "Müşteri Memnuniyeti",
    image: "/reels/1.jpg",
    instagramUrl: "https://www.instagram.com/p/DZsKsfrso0y/",
  },
  {
    title: "Müşteri Memnuniyeti",
    image: "/reels/2.jpg",
    instagramUrl: "https://www.instagram.com/p/DZg4oDjMrok/",
  },
  {
    title: "Müşteri Memnuniyeti",
    image: "/reels/3.jpg",
    instagramUrl: "https://www.instagram.com/p/DZlMh4NxJyB/",
  },
  {
    title: "Müşteri Memnuniyeti",
    image: "/reels/4.jpg",
    instagramUrl: "https://www.instagram.com/p/DZ9wTcMCZbs/",
  },
  {
    title: "Müşteri Memnuniyeti",
    image: "/reels/5.jpg",
    instagramUrl: "https://www.instagram.com/p/DZfvftEsM_l/",
  },
];

const INSTAGRAM_PROFILE = "https://www.instagram.com/ionaumraniyeguzellik";

export default function ReelsSection() {
  const scrollerRef = useRef(null);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-reel-card]");
    const step = card ? card.getBoundingClientRect().width + 16 : 320;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section
      id="memnuniyet"
      data-testid="reels-section"
      className="py-10 md:py-20 bg-gradient-to-b from-white via-[#FAF7FD] to-white"
    >
      <div className="iona-container">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 md:gap-6 mb-6 md:mb-12">
          <div className="max-w-xl">
            <div className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#5E2A84] mb-2 md:mb-4">
              — İnstagram Reels
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-5xl text-[#1A1A1A] leading-[1.15] tracking-tight mb-2 md:mb-4">
              Müşteri <span className="italic text-[#5E2A84]">Memnuniyetleri</span>
            </h2>
            <p className="hidden md:block text-[#4A4A4A] leading-relaxed">
              Danışanlarımızın deneyimlerini Instagram Reels videolarımızdan
              izleyebilirsiniz.
            </p>
          </div>

          <div className="flex items-center gap-3 self-start md:self-end">
            {/* Desktop arrows */}
            <div className="hidden md:flex items-center gap-2">
              <button
                onClick={() => scrollBy(-1)}
                data-testid="reels-prev-btn"
                aria-label="Önceki"
                className="w-12 h-12 rounded-full border border-[#E5E5E5] bg-white text-[#5E2A84] hover:bg-[#F4F1F9] hover:border-[#5E2A84]/30 flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => scrollBy(1)}
                data-testid="reels-next-btn"
                aria-label="Sonraki"
                className="w-12 h-12 rounded-full border border-[#E5E5E5] bg-white text-[#5E2A84] hover:bg-[#F4F1F9] hover:border-[#5E2A84]/30 flex items-center justify-center transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            <a
              href={INSTAGRAM_PROFILE}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reels-instagram-cta"
            >
              <Button className="rounded-full h-12 px-6 bg-[#5E2A84] hover:bg-[#4a2168] text-white iona-soft-shadow">
                <Instagram className="w-4 h-4 mr-2" />
                Instagram'da İzle
              </Button>
            </a>
          </div>
        </div>

        {/* Scroller */}
        <div
          ref={scrollerRef}
          data-testid="reels-scroller"
          className="flex gap-4 md:gap-5 overflow-x-auto snap-x snap-mandatory pb-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          style={{ scrollPaddingLeft: "0px" }}
        >
          {REELS.map((r, i) => (
            <a
              key={`${r.title}-${i}`}
              href={r.instagramUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              data-reel-card
              data-testid={`reel-card-${i}`}
              className="group relative shrink-0 snap-start rounded-2xl overflow-hidden bg-[#1A1A1A] reel-card
                         w-[clamp(140px,42vw,200px)] aspect-[9/16]
                         md:w-[260px] md:aspect-auto md:h-[460px]
                         lg:w-[280px] lg:h-[500px]"
              style={{
                transform: "translateZ(0)",
                willChange: "transform",
              }}
            >
              {/*
                Future-proof: replace <img> below with a <video> when you
                want autoplay video previews:
                <video src={r.video} poster={r.image} muted loop playsInline />
              */}
              <img
                src={r.image}
                alt={r.title}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[1200ms] ease-out group-hover:scale-110 group-active:scale-105"
              />

              {/* Soft purple gradient overlay (light) */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#5E2A84]/35 via-transparent to-transparent opacity-70 group-hover:opacity-85 transition-opacity duration-500 pointer-events-none" />

              {/* Subtle top-down dark overlay for legibility */}
              <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-transparent pointer-events-none" />

              {/* Top-left Instagram badge */}
              <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[#5E2A84] text-[10px] uppercase tracking-wider font-medium z-10">
                <Instagram className="w-3 h-3" />
                Reels
              </div>

              {/* Play indicator with pulse */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                <span className="reel-pulse relative w-14 h-14 rounded-full bg-white/95 backdrop-blur flex items-center justify-center text-[#5E2A84] transition-transform duration-500 group-hover:scale-110">
                  <Play className="w-5 h-5 fill-current ml-0.5" />
                </span>
              </div>

              {/* Title at bottom */}
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 z-10">
                <h3 className="font-serif text-white text-sm md:text-base leading-tight drop-shadow">
                  {r.title}
                </h3>
              </div>
            </a>
          ))}
        </div>

        {/* Mobile hint */}
        <p className="md:hidden text-center text-xs text-[#4A4A4A] mt-4">
          ← Kaydırarak diğer videoları görebilirsiniz →
        </p>
      </div>
    </section>
  );
}
